import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { colors, cardShadow } from "../constants";
import TeamIcon from "@/components/TeamIcon";

// Result Badge Component for Performance Cards
const ResultBadge = ({ type }) => {
  const config = {
    perfect: {
      label: "Perfect",
      bg: "rgba(1, 255, 154, 0.15)",
      border: "rgba(1, 255, 154, 0.3)",
      text: "#01ff9a",
    },
    closeEnough: {
      label: "Close Enough",
      bg: "rgba(250, 204, 21, 0.15)",
      border: "rgba(250, 204, 21, 0.3)",
      text: "#facc15",
    },
    overshoot: {
      label: "Overshoot",
      bg: "rgba(249, 115, 22, 0.15)",
      border: "rgba(249, 115, 22, 0.3)",
      text: "#f97316",
    },
    undershoot: {
      label: "Undershoot",
      bg: "rgba(59, 130, 246, 0.15)",
      border: "rgba(59, 130, 246, 0.3)",
      text: "#3b82f6",
    },
  };

  const style = config[type] || config.perfect;

  return (
    <span
      className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        color: style.text,
      }}
      data-testid={`result-badge-${type}`}
    >
      {style.label}
    </span>
  );
};

// Performance Match Card Component
const PerformanceMatchCard = ({ prediction, matchNumber }) => {
  const {
    team_a,
    team_b,
    predicted_runs,
    actual_runs,
    result_type,
    credits_earned,
    match_status,
    venue,
  } = prediction;

  const isCompleted = match_status === "completed" && actual_runs !== null;

  // Get city from team name
  const getCity = (team) => {
    const TEAM_CITIES = {
      Mumbai: "Mumbai",
      Chennai: "Chennai",
      Rajasthan: "Jaipur",
      Punjab: "Mohali",
      Delhi: "Delhi",
      Hyderabad: "Hyderabad",
      Lucknow: "Lucknow",
      Bangalore: "Bengaluru",
      Gujarat: "Ahmedabad",
      Kolkata: "Kolkata",
    };
    return TEAM_CITIES[team] || team || "Stadium";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`performance-card-${matchNumber}`}
    >
      <div
        className="rounded-[18px] p-5"
        style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          boxShadow: cardShadow,
        }}
      >
        {/* Top Row: Match Number + Location + Result Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-white">M{matchNumber}</span>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-white/50" />
              <span className="text-xs text-white/50">
                {venue?.split(",").pop()?.trim() || getCity(team_a)}
              </span>
            </div>
          </div>
          {isCompleted && result_type && <ResultBadge type={result_type} />}
          {!isCompleted && (
            <span
              className="text-[10px] px-2 py-1 rounded-full"
              style={{
                background: "rgba(255,255,255,0.05)",
                color: colors.textMuted,
              }}
            >
              Pending
            </span>
          )}
        </div>

        {/* Match Info Row: Team Icon + Full Name */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <TeamIcon team={team_a} size="sm" />
            <span className="text-sm font-semibold text-white">{team_a}</span>
          </div>
          <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
            vs
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">{team_b}</span>
            <TeamIcon team={team_b} size="sm" />
          </div>
        </div>

        {/* Earnings Section */}
        {isCompleted && (
          <div className="rounded-xl p-4" style={{ background: "rgba(1, 255, 154, 0.06)" }}>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[11px] mb-1" style={{ color: colors.textMuted }}>
                  Hudle Credits Earned
                </span>
                <span
                  className="text-xl font-bold"
                  style={{
                    background: "linear-gradient(90deg, #01ff9a, #acffe5)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  data-testid="credits-earned"
                >
                  +{credits_earned}
                </span>
              </div>
              <div className="text-right">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                    Total Match Score: <span className="text-white font-medium">{actual_runs}</span>
                  </span>
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>
                    Your Prediction:{" "}
                    <span className="text-white font-medium">{predicted_runs}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Match Info */}
        {!isCompleted && (
          <div
            className="rounded-xl p-4 text-center"
            style={{ background: "rgba(255,255,255,0.03)" }}
          >
            <span className="text-xs" style={{ color: colors.textMuted }}>
              Your Prediction:{" "}
            </span>
            <span className="text-sm font-semibold text-white">{predicted_runs} runs</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PerformanceMatchCard;
